import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Languages, Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { WidgetTranslationDto } from '@/Domain/api/WIdget/dto/WidgetTranslation.dto';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { useUpdateWidgetSettings } from '@/Domain/hooks/Widget/useUpdateWidgetSettings.hook';
import { PreChatSection } from '@/UI/Website/Appearance/PreChat.accordion';
import { TextContentSection } from '@/UI/Website/Appearance/TextContent.accordion';
import { TranslationTarget } from '@/UI/Website/TextContent/LanguageRail';
import { TranslationsSheet } from '@/UI/Website/TextContent/TranslationsSheet';
import { buildPrimaryFormValues } from '@/UI/Website/TextContent/primaryDefaults';
import { emptyTranslation } from '@/UI/Website/TextContent/translationFormValues';

type Props = {
  data: WidgetSettingsDto;
};

const SectionHeader = ({ label, testId }: { label: string; testId?: string }) => (
  <p
    data-testid={testId}
    className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle"
  >
    {label}
  </p>
);

const PrimaryTextContent = ({ data }: { data: WidgetSettingsDto }) => {
  const { t } = useTranslation(['widget', 'common', 'website']);
  const { reset } = usePreview();

  const form = useForm<WidgetSettingsDto>({
    values: buildPrimaryFormValues(data, t),
  });

  useUpdateWidgetSettings({ form, reset });

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-6">
        <SectionHeader label={t('widget:text_content')} testId="section-text-content" />
        <TextContentSection />

        <Separator />

        <section>
          <SectionHeader label={t('widget:prechat')} testId="section-prechat" />
          <PreChatSection />
        </section>
      </form>
    </FormProvider>
  );
};

const TranslationsTrigger = ({ enabled, onOpen }: { enabled: boolean; onOpen: () => void }) => {
  const { t } = useTranslation('website');

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-2xl bg-muted p-4"
      data-testid="translations-trigger"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-card text-foreground-subtle">
          {enabled ? <Languages className="size-4" /> : <Lock className="size-4" />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t('text_content_languages.section_title')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('text_content_languages.section_description')}
          </p>
        </div>
      </div>
      {enabled ? (
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          onClick={onOpen}
          data-testid="open-translations-button"
        >
          {t('text_content_languages.manage_button')}
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          asChild
          className="shrink-0"
          data-testid="upgrade-translations-button"
        >
          <Link to="../subscription">{t('text_content_languages.upgrade_cta')}</Link>
        </Button>
      )}
    </div>
  );
};

export const TextContentPage = ({ data }: Props) => {
  const { t } = useTranslation('website');
  const queryClient = useQueryClient();
  const { getConfig } = useSubscription();
  const { refreshConfiguration } = usePreview();

  const enabled = Boolean(getConfig('widget_translations'));

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selection, setSelection] = useState<TranslationTarget | null>(null);

  const { data: translations = [] } = useQuery<WidgetTranslationDto[]>({
    queryKey: [QueryKey.widgetTranslations.list],
    enabled,
  });

  // Show a rail row for the language being added even before its first save
  // creates a row on the server.
  const displayedTranslations = useMemo(() => {
    if (!selection) return translations;
    const exists = translations.some(
      (row) => row.language === selection.language && row.country === selection.country,
    );
    return exists
      ? translations
      : [...translations, emptyTranslation(selection.language, selection.country)];
  }, [translations, selection]);

  const deleteMutation = useMutation<
    void,
    unknown,
    { language: string; country: string },
    { previous?: WidgetTranslationDto[] }
  >({
    mutationKey: [QueryKey.widgetTranslations.delete],
    // Remove optimistically: the auto-select effect below reads this cache, so a row
    // that lingers until the refetch gets re-selected and displayedTranslations
    // resurrects the just-deleted language as a phantom rail entry.
    onMutate: ({ language, country }) => {
      const previous = queryClient.getQueryData<WidgetTranslationDto[]>([
        QueryKey.widgetTranslations.list,
      ]);
      queryClient.setQueryData(
        [QueryKey.widgetTranslations.list],
        (prev: WidgetTranslationDto[] = []) =>
          prev.filter((row) => !(row.language === language && row.country === country)),
      );
      return { previous };
    },
    onError: (_error, _row, context) => {
      if (context?.previous) {
        queryClient.setQueryData([QueryKey.widgetTranslations.list], context.previous);
      }
      toast.error(t('appearance.form.error.common'));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QueryKey.widgetTranslations.list] }),
  });

  // Persist an empty row as soon as a language is added so it stays in the list
  // (showing "N left") even before any field is translated.
  const createMutation = useMutation<
    WidgetTranslationDto,
    unknown,
    WidgetTranslationDto,
    { previous?: WidgetTranslationDto[] }
  >({
    mutationKey: [QueryKey.widgetTranslations.upsert],
    onMutate: (newRow) => {
      const previous = queryClient.getQueryData<WidgetTranslationDto[]>([
        QueryKey.widgetTranslations.list,
      ]);
      queryClient.setQueryData(
        [QueryKey.widgetTranslations.list],
        (prev: WidgetTranslationDto[] = []) => {
          const others = prev.filter(
            (row) => !(row.language === newRow.language && row.country === newRow.country),
          );
          return [...others, newRow];
        },
      );
      return { previous };
    },
    // Roll back the optimistic row so a failed add doesn't leave a phantom language
    // in the rail that was never persisted. Also drop the selection for that row,
    // else displayedTranslations keeps re-adding it from the still-set selection.
    onError: (_error, newRow, context) => {
      if (context?.previous) {
        queryClient.setQueryData([QueryKey.widgetTranslations.list], context.previous);
      }
      setSelection((prev) =>
        prev && prev.language === newRow.language && prev.country === newRow.country ? null : prev,
      );
      toast.error(t('appearance.form.error.common'));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QueryKey.widgetTranslations.list] }),
  });

  const handleAdd = (language: string) => {
    setSelection({ language, country: '' });
    const exists = translations.some((row) => row.language === language && row.country === '');
    if (!exists) createMutation.mutate(emptyTranslation(language, ''));
  };

  // Auto-select the first language once the sheet is open and translations have
  // loaded (the list may resolve after the sheet opens, so this can't be a
  // one-shot on open).
  useEffect(() => {
    if (sheetOpen && !selection && translations.length > 0) {
      setSelection({ language: translations[0].language, country: translations[0].country });
    }
  }, [sheetOpen, selection, translations]);

  const handleOpenChange = (open: boolean) => {
    setSheetOpen(open);
    // Restore the preview to the saved primary config when closing the sheet.
    if (!open) refreshConfiguration();
  };

  const handleDelete = (language: string, country: string) => {
    deleteMutation.mutate({ language, country });
    setSelection((prev) => {
      if (!prev || prev.language !== language || prev.country !== country) return prev;
      const next = translations.find(
        (row) => !(row.language === language && row.country === country),
      );
      return next ? { language: next.language, country: next.country } : null;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <TranslationsTrigger enabled={enabled} onOpen={() => handleOpenChange(true)} />
      {enabled && (
        <TranslationsSheet
          open={sheetOpen}
          onOpenChange={handleOpenChange}
          data={data}
          translations={translations}
          displayedTranslations={displayedTranslations}
          selection={selection}
          onSelect={setSelection}
          onAdd={handleAdd}
          onDelete={handleDelete}
        />
      )}

      <PrimaryTextContent data={data} />
    </div>
  );
};
